import Autocomplete from 'components/autocomplete';
import DatePicker from 'components/date-picker';
import LoadingSpinner from 'components/loading-spinner';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import TextField from 'components/text-field';
import { useAccountState } from 'contexts/account/account-context';
import Mui from 'materials';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { FilteredData, GraphData } from 'views/planning-prognosis/features/view-fish-group/types/default-fish-group-graph.type';
import { FishGroup } from 'views/planning-prognosis/features/view-fish-group/types/fish-group.type';
import { PlanningPrognosisProfile } from 'views/planning-prognosis/types/planning-prognosis-profile.type';

import { Box, Button, Grid } from '@mui/material';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import { getProfilesAssortment } from 'views/planning-prognosis/api/get-profiles';
import { getPlanningPrognosisSite } from 'views/planning-prognosis/api/get-site';
import { getFishGroups } from 'views/planning-prognosis/api/get-site-fish-groups';
import { getFishGroupSpecies } from 'views/planning-prognosis/features/view-fish-group/api/get-fish-group-species';
import { truncateDecimal } from '../../../../../../utils/validate-numbers';
import { postQuickSim } from '../../api/create-quick-simulation';
import { CreateQuickSimInput } from '../../types/create-quick-sim-input.type';
import CalculatorGraph from './calculator-graph';

type Props = {};

type ParamTypes = {
  localSiteId: string;
  siteTab: string;
  fishGroupTab: Tabs;
};

enum Tabs {
  FISH_GROUPS = 'fishgroups',
  VIRTUAL = 'virtual',
  HARVESTED = 'harvested',
  PROFILES = 'profiles',
  CALCULATOR = 'calculator',
}

const Calculator: React.FC<Props> = () => {
  const intl = useIntl();
  const { account } = useAccountState();
  const { localSiteId } = useParams<ParamTypes>();
  const PAGE_NAME_ID = 'planningPrognosis.calculator';

  // #region STATES

  const [fishGroup, setFishGroup] = useState<FishGroup>();
  const [graph, setGraph] = useState<FilteredData>([]);

  // #endregion STATES

  // #region FETCH DATA

  const { data: site } = useGet(['getPlanningPrognosisSite', account.company?.companyId, localSiteId], ({ queryKey }) =>
    getPlanningPrognosisSite(String(queryKey[1]), String(queryKey[2]))
  );

  const { data: fishGroups } = useGet(['getFishGroups', site?.globalSiteId], (context) => getFishGroups(String(context.queryKey[1])), {
    enabled: !!site,
  });

  const { data: species } = useGet(
    ['getFishGroupSpecies', account.company?.marketId],
    ({ queryKey }) => getFishGroupSpecies(Number(queryKey[1])),
    {
      enabled: !!account.company?.marketId,
    }
  );

  const { data: profiles } = useGet(['getProfilesAssortment', account.company?.marketId, account.company?.companyId], ({ queryKey }) =>
    getProfilesAssortment(Number(queryKey[1]), String(queryKey[2]))
  );

  // #region FORMS

  const { handleSubmit, control, reset, watch } = useForm<CreateQuickSimInput>({
    defaultValues: useMemo(() => {
      return {
        latitude: site?.lattitude,
        speciesId: fishGroup?.speciesId,
        startDate: '',
        quantity: fishGroup?.stockingNumber,
        meanWeight: fishGroup?.stockingWeight,
        egiProfileId: fishGroup?.profileEgi,
        efiProfileId: fishGroup?.profileEfi,
        temperatureProfileId: fishGroup?.profileTemp,
        mortalityProfileId: fishGroup?.profileMort,
      };
    }, [fishGroup, site]),
    shouldUnregister: false,
  });

  // #endregion FORMS

  // #endregion FETCH DATA

  // #region POST DATA

  const calculate = usePost<GraphData[], { globalCompanyId: string; request: CreateQuickSimInput }>(
    (request) => postQuickSim(request.globalCompanyId, request.request),
    (success) => setGraph([{ name: 'Actual', data: success }]),
    (error) => console.log(error)
  );

  const handleCalculate = (data: CreateQuickSimInput) => {
    calculate.mutate({ globalCompanyId: account.company?.companyId || '', request: data });
  };

  const sortBy = (list: PlanningPrognosisProfile[] | undefined) =>
    list ? list.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())) : [];

  // #endregion POST DATA

  return (
    <Grid container>
      <form
        onSubmit={handleSubmit((data) => {
          handleCalculate(data);
        })}>
        <Grid container rowGap={4} spacing={4}>
          <Grid size={12}>
            <Autocomplete
              fullWidth
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.fishGroup` })}
              value={fishGroup}
              options={fishGroups || []}
              getOptionLabel={(option) => {
                return option ? `${(option as FishGroup).fishGroupName} (#${(option as FishGroup).unitId})` : '';
              }}
              onChange={(event, value) => {
                if (value) {
                  const newFishGroup = value as FishGroup;
                  reset((values) => {
                    return {
                      ...values,
                      meanWeight: truncateDecimal(newFishGroup.stockingWeight),
                      quantity: newFishGroup.stockingNumber,
                      efiProfileId: newFishGroup.profileEfi,
                      egiProfileId: newFishGroup.profileEgi,
                      temperatureProfileId: newFishGroup.profileTemp,
                      mortalityProfileId: newFishGroup.profileMort,
                      speciesId: newFishGroup.speciesId,
                    };
                  });

                  setFishGroup(newFishGroup);
                }
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <DatePicker
              fullWidth
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.startDate` })}
              controller={{
                name: 'startDate',
                control: control,
                rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="number"
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.latitude` })}
              controller={{
                name: 'latitude',
                control: control,
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: {
                    positive: (v) =>
                      parseInt(v) >= -90 && parseInt(v) <= 90
                        ? true
                        : intl.formatMessage({ id: 'form.rules.between' }, { lower: '-90', upper: '90' }),
                  },
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="number"
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.quantity` })}
              controller={{
                name: 'quantity',
                control: control,
                rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              type="number"
              fullWidth
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.meanWeight` })}
              controller={{
                name: 'meanWeight',
                control: control,
                rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Select
              fullWidth
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.egiProfile` })}
              controller={{
                control: control,
                name: 'egiProfileId',
                defaultValue: '',
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: {
                    invalidId: (v: number) => (v === 0 ? false : true),
                  },
                },
              }}>
              {sortBy(profiles?.egi).map((egi) => (
                <RadioSelectItem key={egi.id} value={egi.id} selected={egi.id === watch('egiProfileId')}>
                  {egi.name}
                </RadioSelectItem>
              ))}
            </Select>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Select
              fullWidth
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.efiProfile` })}
              controller={{
                name: 'efiProfileId',
                control: control,
                defaultValue: '',
              }}>
              {sortBy(profiles?.efi).map((s) => (
                <RadioSelectItem key={s.id} value={s.id} selected={s.id === watch('efiProfileId')}>
                  {s.name}
                </RadioSelectItem>
              ))}
            </Select>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }} columnGap={2}>
            <Select
              fullWidth
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.species` })}
              controller={{
                control: control,
                name: 'speciesId',
                defaultValue: '',
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: {
                    invalidId: (v: number) => (v === 0 ? false : true),
                  },
                },
              }}
              onChange={() => {}}>
              {species?.map((specie) => (
                <RadioSelectItem key={specie.id} value={specie.id} selected={specie.id === watch('speciesId')}>
                  {specie.name}
                </RadioSelectItem>
              ))}
            </Select>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Select
              fullWidth
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.mortProfile` })}
              controller={{
                name: 'mortalityProfileId',
                control: control,
                defaultValue: '',
              }}>
              {sortBy(profiles?.mort).map((s) => (
                <RadioSelectItem key={s.id} value={s.id} selected={s.id === watch('mortalityProfileId')}>
                  {s.name}
                </RadioSelectItem>
              ))}
            </Select>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }} spacing={12}>
            <Select
              fullWidth
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.tempProfile` })}
              controller={{
                control: control,
                name: 'temperatureProfileId',
                defaultValue: '',
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: {
                    invalidId: (v: number) => (v === 0 ? false : true),
                  },
                },
              }}>
              {sortBy(profiles?.temp).map((temp) => (
                <RadioSelectItem key={temp.id} value={temp.id} selected={temp.id === watch('temperatureProfileId')}>
                  {temp.name}
                </RadioSelectItem>
              ))}
            </Select>
          </Grid>
        </Grid>
        <Box display="flex" sx={{ color: 'white' }} justifyContent={'flex-end'} alignItems="center" paddingTop={4} gap={4}>
          <FormattedMessage id={`${PAGE_NAME_ID}.harvestTarget`} />
          <Button type="submit" size="small">
            <FormattedMessage id={`${PAGE_NAME_ID}.calculate`} />
          </Button>
        </Box>
      </form>
      {calculate.isPending ? <LoadingSpinner overlay /> : !!graph?.length ? <CalculatorGraph graphs={graph} /> : null}
    </Grid>
  );
};

export default Calculator;
